/**
 * Wallet addresses as they are stored and compared — bare addresses, not the
 * union `BLOCKCHAIN:address` ids that `./address.ts` deals with.
 *
 * EVM addresses are hex and case-insensitive, so they are kept lowercase.
 * A Solana address is base58, where case is part of the value: lowercasing one
 * names a different account, or nothing at all. Comparing the two kinds with a
 * single `toLowerCase()` silently locked Solana viewers out of their own
 * profiles, streams and giveaways.
 */

/** Whether this bare address is EVM hex rather than base58. */
function isHexAddress(address: string): boolean {
  return /^0x/i.test(address)
}

/**
 * The stored form of an address: lowercase for EVM, untouched for Solana.
 *
 * Use it on both sides of a comparison and on every write, so a value written
 * by one route is found by another.
 */
export function normalizeWalletAddress(address: string): string {
  return isHexAddress(address) ? address.toLowerCase() : address
}

/** Whether two bare addresses name the same account. */
export function isSameWalletAddress(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false
  return normalizeWalletAddress(a) === normalizeWalletAddress(b)
}
